import { useGlobalState } from '../../Contexts/globalStateContext';
import { hasModuleAccess } from '../../Services/access';
import { AccessTypeRight } from '../../Types/access';
import { useBizMutation, useBizQuery } from '../../Hooks/useBizQuery';
import { executeCatalogCommand, getCatalogConfiguration } from '../../Services/mediaCatalog';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { VirtualFolder } from '../../Types/mediaCatalog';
import { BizButton } from '../Mini/BizButton';
import { getMediaFolderOptions } from '../../Utils/mediaFolders';
import { BizNativeSelect } from '../Mini/BizSelect';
import './media-catalog.scss';

export default function MediaFolderSelect({
	value,
	onChange,
	disabled,
}: {
	value: number | null;
	onChange: (value: number | null) => void;
	disabled?: boolean;
}) {
	const { t } = useTranslation();
	const { state } = useGlobalState();
	const allowed = hasModuleAccess(state.user, 'MEDIABANK', AccessTypeRight.Write);
	const configuration = useBizQuery(['MEDIA_CATALOG', 'CONFIGURATION'], getCatalogConfiguration, allowed);
	const [editing, setEditing] = useState<VirtualFolder | null>(null);
	const titleId = useId();
	const command = useBizMutation(executeCatalogCommand, { getQueryKeyForInvalidation: () => [['MEDIA_CATALOG']] });
	const busy = command.statusState === 'LOADING';
	const folders = configuration.data?.folders ?? [];
	const options = getMediaFolderOptions(folders);
	const excluded = new Set<number>(editing?.id ? [editing.id] : []);
	let changed = true;
	while (changed) {
		changed = false;
		for (const folder of folders) {
			if (folder.parentId !== null && excluded.has(folder.parentId) && !excluded.has(folder.id)) {
				excluded.add(folder.id);
				changed = true;
			}
		}
	}
	const save = async (remove: boolean) => {
		if (!editing) return;
		try {
			await command.mutateAsync({ action: remove ? 'deleteFolder' : 'saveFolder', folder: editing });
			if (remove && value === editing.id) onChange(null);
			await configuration.refetch();
			setEditing(null);
		} catch {
			/* The mutation exposes the server error below. */
		}
	};
	if (!allowed || !configuration.data || folders.length === 0) return null;
	return (
		<div className='biz-media-folder-select'>
			<div className='folder-select-row'>
				<label>
					{t('MEDIABANK.CATALOG.FOLDER')}
					<BizNativeSelect
						selectedId={value ?? 0}
						onChange={(option) => onChange(Number(option?.Id) || null)}
						disabled={disabled || !configuration.data}
						options={[{ Id: 0, Name: t('MEDIABANK.CATALOG.UNSORTED') }, ...getMediaFolderOptions(configuration.data?.folders ?? [])]}
					/>
				</label>
				<button
					type='button'
					className='folder-manage'
					title={t('MEDIABANK.CATALOG.MANAGE_FOLDERS')}
					aria-label={t('MEDIABANK.CATALOG.MANAGE_FOLDERS')}
					disabled={disabled || !configuration.data}
					onClick={() =>
						setEditing({ ...(folders.find((f) => f.id === value) ?? { id: 0, parentId: null, name: '' }) })
					}
				>
					<svg width='18' height='18' viewBox='0 0 24 24' aria-hidden='true'>
						<path
							fill='currentColor'
							d='M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm17.71-10.2a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z'
						/>
					</svg>
				</button>
			</div>
			{configuration.errorMessage && <p role='alert'>{configuration.errorMessage}</p>}
			{editing && (
				<Dialog open fullWidth maxWidth='sm' aria-labelledby={titleId} onClose={() => !busy && setEditing(null)}>
					<DialogTitle id={titleId}>{t('MEDIABANK.CATALOG.MANAGE_FOLDERS')}</DialogTitle>
					<DialogContent>
						<div className='biz-media-folder-select'>
							<label>
								{t('MEDIABANK.CATALOG.EDIT_FOLDER')}
								<BizNativeSelect
									selectedId={editing.id}
									disabled={busy}
									options={[{ Id: 0, Name: t('MEDIABANK.CATALOG.CREATE_NEW_FOLDER') }, ...options]}
									onChange={(o) =>
										setEditing({
											...(folders.find((f) => f.id === Number(o?.Id)) ?? {
												id: 0,
												parentId: null,
												name: '',
											}),
										})
									}
								/>
							</label>
							<label>
								{t('MEDIABANK.CATALOG.FOLDER_NAME')}
								<input
									value={editing.name}
									maxLength={200}
									disabled={busy}
									onChange={(e) => setEditing({ ...editing, name: e.target.value })}
								/>
							</label>
							<label>
								{t('MEDIABANK.CATALOG.PARENT_FOLDER')}
								<BizNativeSelect
									selectedId={editing.parentId ?? 0}
									disabled={busy}
									options={[{ Id: 0, Name: t('MEDIABANK.CATALOG.ROOT_LEVEL') }, ...options.filter((f) => !excluded.has(f.Id))]}
									onChange={(o) => setEditing({ ...editing, parentId: Number(o?.Id) || null })}
								/>
							</label>
							{command.errorMessage && <p role='alert'>{command.errorMessage}</p>}
							<div className='folder-editor-actions'>
								<BizButton
									text={t('MEDIABANK.CATALOG.SAVE_FOLDER')}
									disabled={busy || !editing.name.trim()}
									onClick={() => void save(false)}
								/>
								{editing.id > 0 && (
									<BizButton text={t('MEDIABANK.CATALOG.DELETE_EMPTY_FOLDER')} disabled={busy} onClick={() => void save(true)} />
								)}
								<BizButton text={t('GLOBAL.CLOSE')} disabled={busy} onClick={() => setEditing(null)} />
							</div>
						</div>
					</DialogContent>
				</Dialog>
			)}
		</div>
	);
}
